import { useCallback, useMemo, useState } from "react";
import {
  Room,
  RemoteParticipant,
  DataPacket_Kind,
  Track,
} from "livekit-client";
import { useParticipants } from "@livekit/components-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Mic, MicOff, Video, VideoOff, LogOut, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface ControlPanelProps {
  room: Room;
  isOpen: boolean;
  onClose: () => void;
}

export function ControlPanel({ room, isOpen, onClose }: ControlPanelProps) {
  const participants = useParticipants();
  const [muteAllMic, setMuteAllMic] = useState(false);
  const [muteAllCamera, setMuteAllCamera] = useState(false);
  const [lockUserActions, setLockUserActions] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 24;

  const allParticipants = useMemo(() => {
    return [
      room.localParticipant,
      ...Array.from(room.remoteParticipants.values()),
    ];
  }, [room, participants]);

  const filteredParticipants = useMemo(() => {
    if (!searchQuery.trim()) return allParticipants;
    const query = searchQuery.toLowerCase();
    return allParticipants.filter(
      (p) =>
        p.name?.toLowerCase().includes(query) ||
        p.identity?.toLowerCase().includes(query)
    );
  }, [allParticipants, searchQuery]);

  const paginatedParticipants = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredParticipants.slice(start, end);
  }, [filteredParticipants, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredParticipants.length / itemsPerPage);

  const handleMuteParticipant = useCallback(
    async (participant: RemoteParticipant) => {
      try {
        await participant.setVolume(0);
      } catch (error) {
        console.error("Error muting participant:", error);
      }
    },
    []
  );

  const handleDisableCamera = useCallback(
    async (participant: RemoteParticipant) => {
      try {
        await participant.setVolume(0, Track.Source.Microphone);
      } catch (error) {
        console.error("Error disabling camera:", error);
      }
    },
    []
  );

  const handleRemoveParticipant = useCallback(() => {
    console.log("remove participant", room);
  }, []);

  const getParticipantRole = useCallback(
    (participant: RemoteParticipant) => {
      // TODO: Get role from participant metadata or room state
      if (participant.identity === room.localParticipant.identity) {
        return "Quản trị viên";
      }
      return "Người dùng";
    },
    [room]
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="md:max-w-[1400px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Bảng điều khiển</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6">
          {/* Cài đặt chung */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Cài đặt chung</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="mute-all-mic" className="cursor-pointer">
                  Tắt mic tất cả mọi người
                </Label>
                <Switch
                  id="mute-all-mic"
                  checked={muteAllMic}
                  onCheckedChange={setMuteAllMic}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="mute-all-camera" className="cursor-pointer">
                  Tắt camera tất cả mọi người
                </Label>
                <Switch
                  id="mute-all-camera"
                  checked={muteAllCamera}
                  onCheckedChange={setMuteAllCamera}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="lock-actions" className="cursor-pointer">
                  Khóa thao tác của người dùng
                </Label>
                <Switch
                  id="lock-actions"
                  checked={lockUserActions}
                  onCheckedChange={setLockUserActions}
                />
              </div>
            </div>
          </div>

          {/* Tìm kiếm */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Tìm tên người tham gia hoặc email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-10"
              aria-label="Tìm kiếm người tham gia"
            />
          </div>

          {/* Danh sách người tham gia */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">
              Danh sách người tham gia ({filteredParticipants.length})
            </h3>
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        Họ và tên
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        Vai Trò
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        Trạng thái mạng
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        Âm lượng loa
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium">
                        Thao Tác
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedParticipants.map((participant) => {
                      const isLocal =
                        participant.identity === room.localParticipant.identity;
                      const name =
                        participant.name || participant.identity || "Unknown";
                      const isMicEnabled = participant.isMicrophoneEnabled;
                      const isCameraEnabled = participant.isCameraEnabled;

                      return (
                        <tr
                          key={participant.identity}
                          className="border-t hover:bg-muted/50"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="size-8">
                                <AvatarFallback>
                                  {name.charAt(0).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="font-medium">{name}</div>
                                <div className="text-sm text-muted-foreground">
                                  {participant.identity}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">role</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="size-2 rounded-full bg-green-500" />
                              <span className="text-sm">Đang họp</span>
                              <span className="text-xs text-muted-foreground">
                                (20Mbs/15Mbs)
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 w-32">
                              <Slider
                                defaultValue={[20]}
                                max={100}
                                className="flex-1"
                              />
                              <span className="text-sm w-8 text-right">20</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {!isLocal && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleMuteParticipant(
                                        participant as RemoteParticipant
                                      )
                                    }
                                    aria-label="Tắt mic"
                                    disabled={!isMicEnabled}
                                  >
                                    <MicOff className="size-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleDisableCamera(
                                        participant as RemoteParticipant
                                      )
                                    }
                                    aria-label="Tắt camera"
                                    disabled={!isCameraEnabled}
                                  >
                                    <VideoOff className="size-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleRemoveParticipant()
                                    }
                                    aria-label="Rời khỏi cuộc họp"
                                  >
                                    <LogOut className="size-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Hiển thị {paginatedParticipants.length} /{" "}
                  {filteredParticipants.length} người tham gia
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    aria-label="Trang đầu"
                  >
                    &lt;&lt;
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Trang trước"
                  >
                    &lt;
                  </Button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        aria-label={`Trang ${page}`}
                      >
                        {page}
                      </Button>
                    );
                  })}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    aria-label="Trang sau"
                  >
                    &gt;
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    aria-label="Trang cuối"
                  >
                    &gt;&gt;
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
